import React from 'react';
import { AgentUsage } from '../../hooks/useAgentStream';

interface SessionTotals {
  claude: { tokens: number; cost: number };
  gemini: { tokens: number; cost: number };
}

interface OrgSpend {
  org_name: string;
  total_spend_gbp: number;
  spend_cap_gbp: number;
}

interface Props {
  latestUsage: AgentUsage | null;
  sessionTotals: SessionTotals;
  isSuperAdmin: boolean;
  orgSpend: OrgSpend | null;
}

export const UsageDashboard: React.FC<Props> = ({ sessionTotals, isSuperAdmin, orgSpend }) => {
  const fmt2 = (n: number) => `£${n.toFixed(2)}`;
  const fmt4 = (n: number) => `£${n.toFixed(4)}`;

  const sessionTotal = sessionTotals.claude.cost + sessionTotals.gemini.cost;

  const remainingRaw = orgSpend && orgSpend.spend_cap_gbp
    ? Math.max(0, 100 - (orgSpend.total_spend_gbp / orgSpend.spend_cap_gbp) * 100)
    : null;
  const remainingPct = remainingRaw !== null ? `${remainingRaw.toFixed(5)}%` : '…';
  const barPct = remainingRaw !== null ? remainingRaw : 100;
  const barColor = barPct <= 10 ? '#dc2626' : barPct <= 30 ? '#d97706' : '#903163';

  const firmName = orgSpend?.org_name || null;
  const badgeLabel = isSuperAdmin ? 'Usage' : firmName ? `${firmName}'s Usage` : "Your Firm's Usage";

  // Compact bar for inline use
  const AllowanceBar = ({ width }: { width: number }) => (
    <div style={{ width, height: 6, background: 'var(--c-border)', borderRadius: 3, overflow: 'hidden', flexShrink: 0 }}>
      <div style={{ width: `${barPct}%`, height: '100%', background: barColor, borderRadius: 3, transition: 'width 0.4s' }} />
    </div>
  );

  return (
    <div className="ai-usage-trigger">
      <div className="ai-usage-badge-trigger">
        <span className="ai-pulse" />
        <span>{badgeLabel}</span>
      </div>
      <div className="ai-usage-dashboard">
        <div className="ai-usage-header">
          <span className="ai-pulse" />
          <span>{badgeLabel}</span>
        </div>

        {isSuperAdmin ? (
          <>
            {/* Desktop layout — super admin */}
            <div className="ai-usage-row-main ai-usage-desktop" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
              <span className="ai-usage-label"><span className="ai-label-full">Gemini:</span><span className="ai-label-short">Gem:</span></span>
              <span className="ai-usage-cost">{fmt2(sessionTotals.gemini.cost)}</span>
              <span className="ai-usage-label ai-usage-claude-label"><span className="ai-label-full">Claude:</span><span className="ai-label-short">Cla:</span></span>
              <span className="ai-usage-cost">{fmt2(sessionTotals.claude.cost)}</span>
              <span style={{ borderLeft: '1px solid var(--c-border)', paddingLeft: 12, display: 'flex', gap: 6, alignItems: 'center' }}>
                <span className="ai-usage-label" style={{ fontWeight: 700 }}><span className="ai-label-full">Total:</span><span className="ai-label-short">Tot:</span></span>
                <span className="ai-usage-cost" style={{ fontWeight: 700 }}>{fmt4(sessionTotal)}</span>
              </span>
              <span style={{ borderLeft: '1px solid var(--c-border)', paddingLeft: 12, display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                <span className="ai-usage-label"><span className="ai-label-full">Allowance:</span><span className="ai-label-short">Allw:</span></span>
                <span className="ai-usage-cost">
                  {orgSpend
                    ? `£${orgSpend.total_spend_gbp.toFixed(4)} / £${orgSpend.spend_cap_gbp.toFixed(2)}`
                    : '…'}
                </span>
              </span>
            </div>

            {/* Mobile layout — super admin, 2 rows */}
            <div className="ai-usage-mobile">
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--c-text-muted)', minWidth: 44 }}>Usage</span>
                <span style={{ fontSize: 12 }}><span style={{ color: 'var(--c-text-muted)' }}>Gemini: </span><span className="ai-usage-cost" style={{ fontSize: 12 }}>{fmt2(sessionTotals.gemini.cost)}</span></span>
                <span style={{ fontSize: 12 }}><span style={{ color: 'var(--c-text-muted)' }}>Claude: </span><span className="ai-usage-cost" style={{ fontSize: 12 }}>{fmt2(sessionTotals.claude.cost)}</span></span>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 3 }}>
                <span style={{ minWidth: 44 }} />
                <span style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>Total: <span className="ai-usage-cost" style={{ fontSize: 12, fontWeight: 700 }}>{fmt4(sessionTotal)}</span></span>
                <span style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>Allowance: <span className="ai-usage-cost" style={{ fontSize: 12 }}>
                  {orgSpend ? `£${orgSpend.total_spend_gbp.toFixed(4)} / £${orgSpend.spend_cap_gbp.toFixed(2)}` : '…'}
                </span></span>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Desktop layout — non-super-admin */}
            <div className="ai-usage-row-main ai-usage-desktop" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
              <span className="ai-usage-label"><span className="ai-label-full">Allowance remaining:</span><span className="ai-label-short">Remaining:</span></span>
              <AllowanceBar width={80} />
              <span className="ai-usage-cost">{remainingPct}</span>
            </div>

            {/* Mobile layout — non-super-admin */}
            <div className="ai-usage-mobile">
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--c-text-muted)', whiteSpace: 'nowrap' }}>{badgeLabel}</span>
                <span style={{ fontSize: 12, color: 'var(--c-text-muted)', whiteSpace: 'nowrap' }}>Remaining:</span>
                <AllowanceBar width={48} />
                <span className="ai-usage-cost" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{remainingPct}</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
